import apiClient from '@/repositories/apiClient';
import { api } from './api';

export const deviceApi = api.injectEndpoints({
  endpoints: (build) => ({
    // Paginated, filtered and sorted device overview ("Geräte" / "Meine
    // Geräte"). Backend scopes non-admins to their organisation's devices.
    getDevices: build.query({
      query: (
        query = { pageIndex: 1, search: '', sortBy: [], pageSize: 15, filters: [] },
      ) => ({
        url: 'devices',
        method: 'POST',
        body: JSON.stringify(query),
      }),
      providesTags: (result) =>
        result?.data?.records
          ? [
              ...result.data.records.map((device) => ({
                type: 'Devices',
                id: device.model.ID,
              })),
              { type: 'Devices', id: 'LIST' },
            ]
          : [{ type: 'Devices', id: 'LIST' }],
    }),
    getDevice: build.query({
      query: (id) => ({
        url: `device/${id}`,
      }),
      providesTags: (_result, _err, id) => [{ type: 'Devices', id }],
    }),
    updateDevice: build.mutation({
      query: ({id, form: {
        label: label,
        name: name,
        mirror_server_id: mirror_server_id,
        orientation: orientation,
        sound_output: sound_output,
        settings: settings,
        location_id: location_id,
        stream: {model: {ID: stream_id}},
        model: {ID: ID},
      }}) => ({
        url: `device/${id}`,
        method: 'PUT',
        body: JSON.stringify({ label, name, mirror_server_id, orientation, sound_output, settings, location_id, model: {ID}, stream: {model: {ID: stream_id}}}),
      }),
      // Invalidate both the LIST tag and the concrete device id. `getDevice`
      // tags itself with `{ type: 'Devices', id }`, so a LIST-only
      // invalidation would leave a stale per-device cache and the next
      // form open would render the pre-save snapshot (e.g. old theme).
      invalidatesTags: (result, error, arg) => error ? [] : [
        { type: 'Location', id: result.data.location_id },
        { type: 'Devices', id: 'LIST' },
        { type: 'Devices', id: arg.id },
      ],
    }),
    assignDevice: build.mutation({
      query: (form) => ({
        url: 'device/assign',
        method: 'POST',
        body: JSON.stringify({...form}),
      }),
      invalidatesTags: (result, error) => error ? [] : [{ type: 'Locations', id: result.data.location_id }, { type: 'Devices', id: 'LIST'}],
    }),
    assignMirrorDevice: build.mutation({
      // RTK Query mutations receive a single argument, so the trigger must be
      // called with one object — not positional args, or all but the first
      // would be dropped and the server would reject an empty key.
      query: ({ mirror_server_device_id, key, label, orientation }) => ({
        url: 'device/mirror_assign',
        method: 'POST',
        body: JSON.stringify({ mirror_server_device_id, key, label, orientation }),
      }),
      invalidatesTags: (result, error) => error ? [] : [ { type: 'Devices', id: result.data.mirror_server_device_id}],
    }),
    deleteDevice: build.mutation({
      query: (id) => ({
        url: `device/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, error) => error ? [] : [{ type: 'Locations', id: 'LIST' }, { type: 'Devices', id: 'LIST'}],
    }),
    fetchDeviceStatistics: build.query({
      query: () => ({
        url: 'device/get_statistics',
      }),
    }),
    // Admin-only device-stream endpoints. The regular Device.Stream
    // assignment is unaffected; these endpoints just add/edit/remove the
    // side-stream that an admin plans to plug into the regular stream as
    // a sub-stream item at the agreed time.
    getDeviceStream: build.query({
      query: (deviceId) => ({
        url: `device/${deviceId}/stream`,
      }),
      providesTags: (_r, _e, deviceId) => [{ type: 'DeviceStream', id: deviceId }],
    }),
    createDeviceStream: build.mutation({
      query: ({ deviceId, ...body }) => ({
        url: `device/${deviceId}/stream`,
        method: 'POST',
        body: JSON.stringify(body),
      }),
      invalidatesTags: (_r, e, { deviceId }) => (e ? [] : [
        { type: 'DeviceStream', id: deviceId },
        { type: 'Streams', id: 'LIST' },
      ]),
    }),
    updateDeviceStream: build.mutation({
      query: ({ deviceId, ...body }) => ({
        url: `device/${deviceId}/stream`,
        method: 'PUT',
        body: JSON.stringify(body),
      }),
      invalidatesTags: (_r, e, { deviceId }) => (e ? [] : [
        { type: 'DeviceStream', id: deviceId },
      ]),
    }),
    deleteDeviceStream: build.mutation({
      query: ({ deviceId }) => ({
        url: `device/${deviceId}/stream`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, e, { deviceId }) => (e ? [] : [
        { type: 'DeviceStream', id: deviceId },
        { type: 'Streams', id: 'LIST' },
      ]),
    }),
  }),
});

// check if a key exsits in the database
export const checkDeviceKey = async (key) => {
  try {
    await apiClient.post('/device/check', JSON.stringify({ key }));
  } catch {
    // fehler
    console.info("error while device checking");
    return false;
  }

  return true
}

// means, it checks for free licences
export const preAssign = async () => {
  try {
    await apiClient.get('/device/pre_assign');
  } catch {
    // fehler
    return false;
  }

  return true
}

// means, it checks for free licences
export const sendCommand = async (sessionID, deviceID, target, command) => {
  try {
    await apiClient.post(`/sse/${sessionID}/device/${deviceID}/command`, JSON.stringify({ target, command }));
  } catch {
    // fehler
    return false;
  }

  return true
}


export const {
  useGetDevicesQuery,
  useGetDeviceQuery,
  useUpdateDeviceMutation,
  useAssignDeviceMutation,
  useAssignMirrorDeviceMutation,
  useDeleteDeviceMutation,
  useFetchDeviceStatisticsQuery,
  useGetDeviceStreamQuery,
  useCreateDeviceStreamMutation,
  useUpdateDeviceStreamMutation,
  useDeleteDeviceStreamMutation,
} = deviceApi;
